import {
  Button,
  Dialog,
  DialogContent,
  FormControl,
  MenuItem,
  InputLabel,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';
import { useGlobalCore } from '../../../../core/modules/global';
import { UPDATE_RECEPTION_DIALOG } from '../../../../core/constants/dialog';
import Spinner from '../../Spinner';
import { useCompanyCore } from '../../../../core/modules/company';
import { useReceptionCore } from '../../../../core/modules/reception';
import moment from 'moment';
import { DOCUMENT_TYPES } from '../../../../core/constants/documentTypes';
import { useMasterCore } from '../../../../core/modules/master';
import { useMeCore } from '../../../../core/modules/me';
import Swal from 'sweetalert2';

const schema = yup.object().shape({
  received_at: yup.string().required('Campo requerido'),
  quantity: yup
    .number()
    .required('Campo requerido')
    .typeError('Formato incorrecto'),
  document_number: yup.string().required('Campo requerido'),
  document_type_id: yup.string().required('Campo requerido'),
  reception_order_id: yup.number().required('Campo requerido'),
  reception_type_id: yup.number().required('Campo requerido'),
  notes: yup.string().optional(),
});

const UpdateDialog = () => {
  const { dialogs, closeDialog } = useGlobalCore();
  const { reception, receptionOrders, putReception, getReceptions, deleteReception } = useReceptionCore();
  const { companyId } = useCompanyCore();
  const { user } = useMeCore();
  const { receptionTypes } = useMasterCore();

  const [loading, setLoading]  = useState(false);

  const canWrite = useMemo(() => user && (user.privileges.superAdmin || user.privileges.writeReception), [user]); 

  const { control, formState, handleSubmit, reset, setValue } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
  });
  const { isValid, errors } = formState;

  const resetForm = useCallback(() => {
    reset({});
    closeDialog(UPDATE_RECEPTION_DIALOG);
  }, [closeDialog, reset]);

  const onSubmit = useCallback(
    (values: any) => {
      if (companyId) {
        setLoading(true);
        putReception(
          companyId,
          reception?.id!,
          {
            ...values,
            document_type_id: +values.document_type_id,
            reception_type_id: +values.reception_type_id,
            received_at: moment(values.received_at).unix()
          },
          () => {
            resetForm();
            getReceptions(companyId);
            setLoading(false);
          },
          () => {
            setLoading(false);
          }
        );
      }
    },
    [companyId, putReception, reception?.id, resetForm, getReceptions]
  );

  const onDelete = useCallback(() => {
    Swal.fire({
      title: "Confimar acción",
      text: `Eliminar la orden de ingreso ${reception?.document_number}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Si, eliminar!",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (result.isConfirmed && companyId && reception) {
        setLoading(true);

        deleteReception(
          companyId,
          reception.id,
          () => {
            setLoading(false);
            getReceptions(companyId);
            resetForm();
          },
          () => {
            setLoading(false);
          }
        );
      }
    });
  }, [reception, companyId, deleteReception, getReceptions, resetForm]);

  useEffect(() => {
    if (reception) {
              setValue("received_at", moment(reception.received_at).format('yyyy-MM-DD'));
      setValue("quantity", reception.quantity);
      setValue("document_number", reception.document_number);
      setValue("document_type_id", reception.document_type_id);
      setValue("reception_order_id", reception.reception_order_id);
      setValue("reception_type_id", reception.reception_type_id);
      setValue("notes", reception.notes);
    }
  }, [reception, setValue]);

  return (
    <Dialog
      classes={{
        paper: 'm-8 sm:m-24 min-w-[350px]',
      }}
      onClose={resetForm}
      open={Array.isArray(dialogs) && Array.isArray(dialogs) && dialogs.includes(UPDATE_RECEPTION_DIALOG)}
    >
      <DialogContent>
        <div className="flex flex-auto flex-col py-16 px-0 sm:px-16">
          <div className="flex flex-col">
            <div className="mb-16 flex items-center">
              <Typography className="text-2xl font-bold truncate">
                Modificar orden de ingreso
              </Typography>
            </div>
      
            <div className="w-full max-w-3xl mt-20">
              <div className="flex flex-col space-y-32">
                <form
                  noValidate
                  onSubmit={handleSubmit(onSubmit)}
                  className="flex flex-col space-y-24"
                >
                  <Controller
                    name="received_at"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Fecha de Recepción"
                        type="date"
                        variant="outlined"
                        required
                        fullWidth
                      />
                    )}
                  />

                  <Controller
                    name="reception_order_id"
                    control={control}
                    render={({ field }) => (
                      <FormControl variant="outlined" fullWidth required>
                        <InputLabel id="type-label">
                          Orden de ingreso
                        </InputLabel>
                        <Select
                          {...field}
                          labelId="type-label"
                          label="Orden de ingreso"
                          disabled={loading}
                          required
                        >
                          {receptionOrders.map((order) => (
                            <MenuItem key={order.id} value={order.id}>{order.description}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />

                  <Controller
                    name="reception_type_id"
                    control={control}
                    render={({ field }) => (
                      <FormControl variant="outlined" fullWidth required>
                        <InputLabel id="type-label">
                          Tipo de recepción
                        </InputLabel>
                        <Select
                          {...field}
                          labelId="type-label"
                          label="Tipo de recepción"
                          disabled={loading}
                          required
                        >
                          {receptionTypes.map((type) => (
                            <MenuItem key={type.id} value={type.id}>{type.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />

                  <Controller
                    name="document_type_id"
                    control={control}
                    render={({ field }) => (
                      <FormControl variant="outlined" fullWidth required>
                        <InputLabel id="type-label">
                          Tipo de documento
                        </InputLabel>
                        <Select
                          {...field}
                          labelId="document_type-label"
                          id="document_type-label"
                          label="Tipo de documento"
                          disabled={loading}
                          required
                        >
                          {DOCUMENT_TYPES.map(({ name, value }) => (
                            <MenuItem key={value} value={value}>
                              {name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />

                  <Controller
                    name="document_number"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Número de Documento"
                        variant="outlined"
                        required
                        fullWidth
                      />
                    )}
                  />

                  <Controller
                    name="quantity"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Cantidad"
                        type="number"
                        variant="outlined"
                        required
                        fullWidth
                      />
                    )}
                  />

                  <Controller
                    name="notes"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        multiline
                        label="Observación"
                        variant="outlined"
                        fullWidth
                      />
                    )}
                  />

                  {canWrite && (
                    <div className="flex justify-center gap-4">
                      {/* <Button
                        variant="contained"
                        color="error"
                        sx={{ borderRadius: 50 }}
                        disabled={loading}
                        type="button"
                        onClick={onDelete}
                      >
                        <Spinner show={loading} />
                        Eliminar
                      </Button> */}
                      <Button
                        variant="contained"
                        color="secondary"
                        sx={{ borderRadius: 50 }}
                        disabled={!isEmpty(errors) || !isValid || loading}
                        type="submit"
                        size="large"
                      >
                        <Spinner show={loading} />
                        Guardar
                      </Button>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateDialog;
